import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function GameSetup() {
  const { gameSlug } = useParams();
  const [searchParams] = useSearchParams();
  const existingActiveGameId = searchParams.get('activeGameId');
  const isEditMode = searchParams.get('editMode') === 'true';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [game, setGame] = useState(null);
  const [activeGame, setActiveGame] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [dealerIndex, setDealerIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [draggedPlayer, setDraggedPlayer] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [creatingPlayer, setCreatingPlayer] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPlayerSearch, setShowPlayerSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [doubleDeckMode, setDoubleDeckMode] = useState(false);
  const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);

  // Track original state for change detection in edit mode
  const [originalPlayerOrder, setOriginalPlayerOrder] = useState([]);
  const [originalDealerIndex, setOriginalDealerIndex] = useState(0);

  // Touch drag state
  const [touchDragIndex, setTouchDragIndex] = useState(null);
  const [touchDragY, setTouchDragY] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const playerListRef = useRef(null);
  const playerRefs = useRef([]);
  const longPressTimer = useRef(null);
  const touchStartY = useRef(0);

  useEffect(() => {
    fetchData();
  }, [gameSlug]);

  // Poll for player updates when we have an active game
  useEffect(() => {
    if (!activeGame) return;

    const pollForUpdates = async () => {
      try {
        const res = await fetch(`${window.API_URL}/api/games/active/${activeGame.id}`);
        if (res.ok) {
          const updatedGame = await res.json();
          setActiveGame(updatedGame);

          // Update selected players from the active game
          if (updatedGame.players && updatedGame.players.length > 0) {
            const validPlayers = updatedGame.players
              .filter(p => p.user_id)
              .map(p => allUsers.find(u => u.id === p.user_id) || { id: p.user_id, name: p.name, avatar_color: p.avatar_color })
              .filter(Boolean);

            // Only update if players have changed
            const currentIds = selectedPlayers.map(p => p.id).sort().join(',');
            const newIds = validPlayers.map(p => p.id).sort().join(',');
            if (currentIds !== newIds) {
              setSelectedPlayers(validPlayers);
            }
          }
        }
      } catch (err) {
        console.error('Error polling for updates:', err);
      }
    };

    const interval = setInterval(pollForUpdates, 2000);
    return () => clearInterval(interval);
  }, [activeGame?.id, allUsers]);

  const fetchData = async () => {
    try {
      // Fetch game info by slug
      const gameRes = await fetch(`${window.API_URL}/api/games/by-slug/${gameSlug}`);
      const gameData = await gameRes.json();
      setGame(gameData.game);

      // Fetch all users
      const usersRes = await fetch(`${window.API_URL}/api/users`);
      const usersData = await usersRes.json();
      setAllUsers(usersData);

      // Check if we're joining an existing active game
      if (existingActiveGameId) {
        // Fetch the existing active game
        const activeRes = await fetch(`${window.API_URL}/api/games/active/${existingActiveGameId}`);
        if (activeRes.ok) {
          const existingGame = await activeRes.json();
          setActiveGame(existingGame);

          // Set selected players from existing game
          if (existingGame.players && existingGame.players.length > 0) {
            const validPlayers = existingGame.players
              .filter(p => p.user_id)
              .map(p => usersData.find(u => u.id === p.user_id))
              .filter(Boolean);
            setSelectedPlayers(validPlayers);

            // Set dealer index
            const dealerPlayer = existingGame.players.find(p => p.is_dealer);
            let dealerIdx = 0;
            if (dealerPlayer) {
              dealerIdx = validPlayers.findIndex(p => p.id === dealerPlayer.user_id);
              if (dealerIdx >= 0) setDealerIndex(dealerIdx);
            }

            // In edit mode, save original state for change detection
            if (isEditMode) {
              setOriginalPlayerOrder(validPlayers.map(p => p.id));
              setOriginalDealerIndex(dealerIdx >= 0 ? dealerIdx : 0);
            }
          }
          setLoading(false);
          return;
        }
      }

      // Pre-select current user for new game
      const currentUser = usersData.find(u => u.id === user.id);
      if (currentUser) {
        setSelectedPlayers([currentUser]);
      }

      // Create an active game in setup status so we have a join code
      const createRes = await fetch(`${window.API_URL}/api/games/active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          game_id: gameData.game.id,
          host_user_id: user.id
        })
      });
      const newActiveGame = await createRes.json();
      setActiveGame(newActiveGame);

      // Update URL with activeGameId so refresh doesn't create a new game
      window.history.replaceState(null, '', `/game-setup/${gameSlug}?activeGameId=${newActiveGame.id}`);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const getInviteLink = () => {
    if (!activeGame) return '';
    return `${window.location.origin}/join/${activeGame.join_code}`;
  };

  const copyInviteLink = async () => {
    const link = getInviteLink();
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Search for users with trigram
  const searchUsers = async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(`${window.API_URL}/api/users/search?q=${encodeURIComponent(query)}`);
      const results = await res.json();
      // Filter out already selected players
      const filtered = results.filter(u => !selectedPlayers.find(p => p.id === u.id));
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchUsers(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedPlayers]);

  const togglePlayer = (player) => {
    if (selectedPlayers.find(p => p.id === player.id)) {
      setSelectedPlayers(selectedPlayers.filter(p => p.id !== player.id));
      // Adjust dealer index if needed
      const removedIndex = selectedPlayers.findIndex(p => p.id === player.id);
      if (dealerIndex >= selectedPlayers.length - 1) {
        setDealerIndex(Math.max(0, selectedPlayers.length - 2));
      } else if (removedIndex < dealerIndex) {
        setDealerIndex(dealerIndex - 1);
      }
    } else {
      setSelectedPlayers([...selectedPlayers, player]);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedPlayer(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedPlayer === null || draggedPlayer === index) return;
    reorderPlayers(draggedPlayer, index);
    setDraggedPlayer(index);
  };

  const handleDragEnd = () => {
    setDraggedPlayer(null);
  };

  // Reorder players helper
  const reorderPlayers = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    const newPlayers = [...selectedPlayers];
    const player = newPlayers[fromIndex];
    newPlayers.splice(fromIndex, 1);
    newPlayers.splice(toIndex, 0, player);

    // Update dealer index if it was affected
    if (dealerIndex === fromIndex) {
      setDealerIndex(toIndex);
    } else if (fromIndex < dealerIndex && toIndex >= dealerIndex) {
      setDealerIndex(dealerIndex - 1);
    } else if (fromIndex > dealerIndex && toIndex <= dealerIndex) {
      setDealerIndex(dealerIndex + 1);
    }

    setSelectedPlayers(newPlayers);
  };

  // Helper function to detect changes in edit mode
  const getChanges = () => {
    const changes = [];
    const currentOrder = selectedPlayers.map(p => p.id);

    // Check for order changes
    if (originalPlayerOrder.length > 0) {
      for (let i = 0; i < currentOrder.length; i++) {
        const playerId = currentOrder[i];
        const originalIndex = originalPlayerOrder.indexOf(playerId);
        if (originalIndex !== -1 && originalIndex !== i) {
          const player = selectedPlayers.find(p => p.id === playerId);
          changes.push({
            type: 'order',
            message: `${player?.name} moved from position ${originalIndex + 1} to ${i + 1}`
          });
        }
      }
    }

    // Check for dealer change
    if (originalPlayerOrder.length > 0) {
      const originalDealerId = originalPlayerOrder[originalDealerIndex];
      const currentDealerId = currentOrder[dealerIndex];
      if (originalDealerId !== currentDealerId) {
        const newDealer = selectedPlayers.find(p => p.id === currentDealerId);
        const oldDealer = selectedPlayers.find(p => p.id === originalDealerId);
        changes.push({
          type: 'dealer',
          message: `Dealer changed from ${oldDealer?.name || 'unknown'} to ${newDealer?.name}`
        });
      }
    }

    return changes;
  };

  const hasChanges = () => {
    if (!isEditMode || originalPlayerOrder.length === 0) return false;
    const currentOrder = selectedPlayers.map(p => p.id);
    const orderChanged = JSON.stringify(originalPlayerOrder) !== JSON.stringify(currentOrder);
    const dealerChanged = originalPlayerOrder[originalDealerIndex] !== currentOrder[dealerIndex];
    return orderChanged || dealerChanged;
  };

  // Handle back button click in edit mode
  const handleBackClick = () => {
    if (isEditMode && activeGame) {
      if (hasChanges()) {
        setShowSaveChangesModal(true);
      } else {
        // No changes, just go back
        navigate(`/game/${activeGame.id}`);
      }
    } else {
      // Cancel the setup game if navigating away
      if (activeGame && activeGame.status === 'setup') {
        fetch(`${window.API_URL}/api/games/active/${activeGame.id}`, { method: 'DELETE' });
      }
      navigate('/');
    }
  };

  // Save changes and navigate back
  const handleSaveAndGoBack = async () => {
    setShowSaveChangesModal(false);
    await handleStartGame(); // This already handles saving in edit mode
  };

  // Discard changes and navigate back
  const handleDiscardChanges = () => {
    setShowSaveChangesModal(false);
    navigate(`/game/${activeGame.id}`);
  };

  // Touch drag handlers for mobile
  const handleTouchStart = (e, index) => {
    touchStartY.current = e.touches[0].clientY;

    // Long press to start drag (150ms)
    longPressTimer.current = setTimeout(() => {
      const rect = playerRefs.current[index]?.getBoundingClientRect();
      if (rect) {
        setDragOffset(e.touches[0].clientY - rect.top);
      }
      setTouchDragIndex(index);
      setTouchDragY(e.touches[0].clientY);

      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 150);
  };

  const handleTouchMove = (e, index) => {
    // If we haven't started dragging yet, check if user moved too much (scrolling)
    if (touchDragIndex === null) {
      const moveDistance = Math.abs(e.touches[0].clientY - touchStartY.current);
      if (moveDistance > 10) {
        // User is scrolling, cancel long press
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
        }
      }
      return;
    }

    e.preventDefault(); // Prevent scrolling while dragging
    setTouchDragY(e.touches[0].clientY);

    // Find which index we're hovering over
    const listRect = playerListRef.current?.getBoundingClientRect();
    if (!listRect) return;

    let targetIndex = touchDragIndex;
    for (let i = 0; i < playerRefs.current.length; i++) {
      const ref = playerRefs.current[i];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        if (e.touches[0].clientY < midY) {
          targetIndex = i;
          break;
        }
        targetIndex = i + 1;
      }
    }

    targetIndex = Math.max(0, Math.min(targetIndex, selectedPlayers.length - 1));

    if (targetIndex !== touchDragIndex) {
      reorderPlayers(touchDragIndex, targetIndex);
      setTouchDragIndex(targetIndex);
    }
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setTouchDragIndex(null);
    setTouchDragY(0);
  };

  const handleCreatePlayer = async (e) => {
    e.preventDefault();
    if (!newPlayerName.trim()) return;

    setCreatingPlayer(true);
    try {
      const res = await fetch(`${window.API_URL}/api/users/simple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newPlayerName.trim() })
      });

      if (!res.ok) {
        const error = await res.json();
        alert(error.error || 'Failed to create player');
        return;
      }

      const newPlayer = await res.json();
      // Add to users list and select them
      setAllUsers([...allUsers, newPlayer]);
      setSelectedPlayers([...selectedPlayers, newPlayer]);
      setNewPlayerName('');
      setShowInviteModal(false);
    } catch (error) {
      console.error('Error creating player:', error);
      alert('Failed to create player');
    } finally {
      setCreatingPlayer(false);
    }
  };

  const handleStartGame = async () => {
    if (selectedPlayers.length < (game?.min_players || 2)) {
      alert(`Need at least ${game?.min_players || 2} players`);
      return;
    }

    if (!activeGame) {
      alert('Game session not ready. Please refresh and try again.');
      return;
    }

    setCreating(true);

    try {
      // In edit mode, just update order and dealer, don't add players or start
      if (isEditMode) {
        // Update player order and dealer in the players table
        await fetch(`${window.API_URL}/api/games/active/${activeGame.id}/players/order`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            players: selectedPlayers.map((p, i) => ({
              user_id: p.id,
              turn_order: i,
              is_dealer: i === dealerIndex
            }))
          })
        });

        // Also update the game_state.playerOrder to keep them in sync
        // Get the current game state first
        const currentGameRes = await fetch(`${window.API_URL}/api/games/active/${activeGame.id}`);
        const currentGame = await currentGameRes.json();
        const currentState = currentGame.game_state || {};

        const newOrder = selectedPlayers.map(p => p.id);
        const changes = getChanges();

        let updatedGameLog = currentState.gameLog || [];
        if (changes.length > 0) {
          const timestamp = new Date().toLocaleTimeString();
          // Create a single log entry with all changes
          const changeMessages = changes.map(c => c.message);
          updatedGameLog = [{
            id: Date.now(),
            time: timestamp,
            message: changeMessages.length === 1
              ? changeMessages[0]
              : `Settings updated: ${changeMessages.join('; ')}`,
            type: 'settings',
            details: changes // Store individual changes for potential future use
          }, ...updatedGameLog];
        }

        // Update game state with new player order
        await fetch(`${window.API_URL}/api/games/active/${activeGame.id}/state`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            game_state: {
              ...currentState,
              playerOrder: newOrder,
              dealerUserId: selectedPlayers[dealerIndex]?.id,
              gameLog: updatedGameLog
            }
          })
        });

        // Navigate back to the game
        navigate(`/game/${activeGame.id}`);
        return;
      }

      // Add all players with turn order (host is already added)
      for (let i = 0; i < selectedPlayers.length; i++) {
        const player = selectedPlayers[i];
        if (player.id !== user.id) {
          await fetch(`${window.API_URL}/api/games/active/${activeGame.id}/players`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: player.id })
          });
        }
      }

      // Update player order and dealer
      await fetch(`${window.API_URL}/api/games/active/${activeGame.id}/players/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          players: selectedPlayers.map((p, i) => ({
            user_id: p.id,
            turn_order: i,
            is_dealer: i === dealerIndex
          }))
        })
      });

      // Start the game
      await fetch(`${window.API_URL}/api/games/active/${activeGame.id}/start`, {
        method: 'POST'
      });

      // Navigate to the game
      navigate(`/game/${activeGame.id}`);
    } catch (error) {
      console.error('Error creating game:', error);
      alert('Failed to create game');
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  const availablePlayers = allUsers.filter(u => !selectedPlayers.find(p => p.id === u.id));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBackClick}
            className="text-slate-400 hover:text-white"
          >
            ← {isEditMode ? 'Back to Game' : 'Back'}
          </button>
          <div className="flex-1 text-center">
            <span className="text-3xl">{game?.emoji || '🎮'}</span>
            <h1 className="text-2xl font-bold text-white">{game?.name}</h1>
            <p className="text-slate-400 text-sm">{isEditMode ? 'Edit game settings' : 'Set up your game'}</p>
          </div>
          <div className="w-10" /> {/* Spacer for symmetry */}
        </div>

        {/* Join Code Banner - Centered and Clickable */}
        {activeGame && (
          <button
            onClick={copyInviteLink}
            className="w-full bg-blue-600/20 border border-blue-500/30 hover:bg-blue-600/30 rounded-xl p-4 mb-6 transition-colors cursor-pointer"
          >
            <div className="text-center">
              <p className="text-blue-300 text-sm">Game Code {copied && '• Copied!'}</p>
              <p className="text-2xl font-bold text-white tracking-widest">{activeGame.join_code}</p>
              <p className="text-blue-400 text-xs mt-1">Tap to copy invite link</p>
            </div>
          </button>
        )}

        {/* Selected Players - Draggable Order */}
        <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-sm font-semibold text-white">
              Playing Order
              <span className="text-slate-400 ml-2">({selectedPlayers.length} players)</span>
            </h2>
            <p className="text-xs text-slate-400">Hold & drag to reorder</p>
          </div>

          <div className="space-y-2" ref={playerListRef}>
            {selectedPlayers.map((player, index) => (
              <div
                key={player.id}
                ref={el => playerRefs.current[index] = el}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                onTouchStart={(e) => handleTouchStart(e, index)}
                onTouchMove={(e) => handleTouchMove(e, index)}
                onTouchEnd={handleTouchEnd}
                className={`flex items-center gap-2 p-3 rounded-lg transition-all select-none
                  ${dealerIndex === index
                    ? 'bg-amber-500/20 border border-amber-500/50'
                    : 'bg-slate-700/50 border border-transparent'
                  }
                  ${touchDragIndex === index ? 'opacity-70 scale-105 shadow-lg shadow-blue-500/30 border-blue-500 z-50' : ''}
                  ${draggedPlayer === index ? 'opacity-50' : ''}
                `}
                style={touchDragIndex === index ? {
                  position: 'relative',
                  zIndex: 50,
                } : {}}
              >
                {/* Drag Handle */}
                <div className="flex flex-col gap-0.5 px-1 text-slate-500 touch-none">
                  <div className="flex gap-0.5">
                    <div className="w-1 h-1 rounded-full bg-current"></div>
                    <div className="w-1 h-1 rounded-full bg-current"></div>
                  </div>
                  <div className="flex gap-0.5">
                    <div className="w-1 h-1 rounded-full bg-current"></div>
                    <div className="w-1 h-1 rounded-full bg-current"></div>
                  </div>
                  <div className="flex gap-0.5">
                    <div className="w-1 h-1 rounded-full bg-current"></div>
                    <div className="w-1 h-1 rounded-full bg-current"></div>
                  </div>
                </div>

                <div className="text-slate-400 font-mono text-sm w-5 text-center">
                  {index + 1}
                </div>

                <div className="flex items-center gap-3 flex-1">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-semibold"
                    style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                  >
                    {player.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-white font-medium">{player.name}</div>
                    {dealerIndex === index && (
                      <div className="text-amber-400 text-xs">🃏 Dealer</div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setDealerIndex(index)}
                    className={`p-2 rounded-lg transition-colors ${
                      dealerIndex === index
                        ? 'bg-amber-500 text-black'
                        : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                    }`}
                    title="Set as dealer"
                  >
                    🃏
                  </button>
                  <button
                    onClick={() => togglePlayer(player)}
                    className="p-2 rounded-lg bg-red-600/50 text-red-300 hover:bg-red-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}

            {/* Add Player Row */}
            <div className="relative">
              {/* Show Add Player when not at max */}
              {selectedPlayers.length < (doubleDeckMode ? (game?.max_players || 7) * 2 : (game?.max_players || 7)) && (
              <div
                onClick={() => setShowPlayerSearch(!showPlayerSearch)}
                className="flex items-center gap-3 p-3 rounded-lg bg-slate-700/30 border border-dashed border-slate-500 hover:border-slate-400 hover:bg-slate-700/50 transition-colors cursor-pointer"
              >
                <div className="text-slate-500 font-mono text-sm w-6 text-center">
                  +
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center text-slate-400 text-lg">
                    +
                  </div>
                  <div className="text-slate-400">Add Player</div>
                </div>
              </div>
              )}

              {/* Search Dropdown */}
              {showPlayerSearch && (
                <div className="absolute left-0 right-0 mt-2 bg-slate-800 rounded-lg border border-slate-600 shadow-xl z-10">
                  <div className="p-3">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search players..."
                      className="w-full bg-slate-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  </div>

                  <div className="max-h-48 overflow-y-auto">
                    {searching && (
                      <div className="px-3 py-2 text-slate-400 text-sm">Searching...</div>
                    )}

                    {!searching && searchQuery && searchResults.length === 0 && (
                      <div className="px-3 py-2 text-slate-400 text-sm">No players found</div>
                    )}

                    {searchResults.map(player => (
                      <button
                        key={player.id}
                        onClick={() => {
                          togglePlayer(player);
                          setSearchQuery('');
                          setShowPlayerSearch(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-700 transition-colors"
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                          style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                        >
                          {player.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white text-sm">{player.name}</span>
                      </button>
                    ))}
                  </div>

                  {/* Create New Player Option */}
                  <div className="border-t border-slate-700 p-2">
                    <button
                      onClick={() => {
                        setShowPlayerSearch(false);
                        setShowInviteModal(true);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-700 transition-colors text-blue-400"
                    >
                      <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center text-sm">
                        +
                      </div>
                      <span className="text-sm">Create New Player</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Double Deck Button - show when at normal max for supported games */}
            {!doubleDeckMode &&
              (game?.slug === 'five-crowns' || game?.slug === 'phase-10') &&
              selectedPlayers.length >= (game?.max_players || 7) && (
              <button
                onClick={() => setDoubleDeckMode(true)}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-purple-600/30 border border-purple-500/50 hover:bg-purple-600/40 transition-colors cursor-pointer mt-2"
              >
                <div className="text-purple-400 font-mono text-sm w-6 text-center">
                  2×
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white text-lg">
                    🃏
                  </div>
                  <div>
                    <div className="text-purple-200 font-medium">Play Double Deck</div>
                    <div className="text-purple-400 text-xs">Add more players (up to {(game?.max_players || 7) * 2})</div>
                  </div>
                </div>
              </button>
            )}

            {/* Double Deck Mode Indicator */}
            {doubleDeckMode && (
              <div className="flex items-center justify-between px-3 py-2 mt-2 rounded-lg bg-purple-600/20 border border-purple-500/30">
                <div className="flex items-center gap-2">
                  <span className="text-purple-300">🃏🃏</span>
                  <span className="text-purple-200 text-sm font-medium">Double Deck Mode</span>
                  <span className="text-purple-400 text-xs">(up to {(game?.max_players || 7) * 2} players)</span>
                </div>
                <button
                  onClick={() => setDoubleDeckMode(false)}
                  className="text-purple-400 hover:text-purple-200 text-xs"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Start Game / Back to Game Button */}
        <button
          onClick={handleStartGame}
          disabled={creating || selectedPlayers.length < (game?.min_players || 2)}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all
            ${game?.slug === 'five-crowns' ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-black hover:from-emerald-400' :
              game?.slug === 'phase-10' ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 text-black hover:from-cyan-400' :
              'bg-blue-600 text-white hover:bg-blue-500'
            }
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          {creating ? (isEditMode ? 'Saving...' : 'Creating Game...') :
            selectedPlayers.length < (game?.min_players || 2)
              ? `Need ${(game?.min_players || 2) - selectedPlayers.length} more player(s)`
              : isEditMode ? `Save & Return to Game` : `Start ${game?.name}`
          }
        </button>

        <p className="text-center text-slate-400 text-xs mt-4">
          {game?.min_players || 2}-{game?.max_players || 14} players • {game?.description}
        </p>
      </div>

      {/* Add New Player Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-white mb-2">Add New Player</h3>
            <p className="text-slate-400 text-sm mb-4">
              Add a family member or friend who doesn't have an account yet.
            </p>
            <form onSubmit={handleCreatePlayer}>
              <input
                type="text"
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                placeholder="Player name"
                className="w-full bg-slate-700 text-white rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowInviteModal(false);
                    setNewPlayerName('');
                  }}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingPlayer || !newPlayerName.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white py-2.5 rounded-lg font-medium"
                >
                  {creatingPlayer ? 'Adding...' : 'Add Player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Save Changes Modal - shown when leaving edit mode with unsaved changes */}
      {showSaveChangesModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-white mb-2">Save Changes?</h3>
            <p className="text-slate-400 text-sm mb-4">
              You have unsaved changes that will be logged:
            </p>

            {/* List of changes */}
            <div className="bg-slate-700/50 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
              {getChanges().map((change, i) => (
                <div key={i} className="flex items-start gap-2 text-sm py-1">
                  <span className={change.type === 'dealer' ? 'text-amber-400' : 'text-blue-400'}>
                    {change.type === 'dealer' ? '🃏' : '↕️'}
                  </span>
                  <span className="text-slate-200">{change.message}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <button
                onClick={handleSaveAndGoBack}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-lg font-medium"
              >
                Save Changes
              </button>
              <button
                onClick={handleDiscardChanges}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white py-2.5 rounded-lg font-medium"
              >
                Discard Changes
              </button>
              <button
                onClick={() => setShowSaveChangesModal(false)}
                className="w-full text-slate-400 hover:text-white py-2 text-sm"
              >
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
